import { useState, useCallback, useEffect } from 'react'

interface Toast {
  id: string
  title: string
  description?: string
  variant?: 'default' | 'success' | 'error' | 'warning'
  duration?: number
}

interface ToastStore {
  toasts: Toast[]
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void
}

// Simple in-memory store for toasts
let toasts: Toast[] = []
let listeners: (() => void)[] = []

function emitChange() {
  listeners.forEach((listener) => listener())
}

export function useToastStore(): ToastStore {
  const [, setTick] = useState(0)

  useEffect(() => {
    const listener = () => setTick((t) => t + 1)
    listeners.push(listener)
    return () => {
      listeners = listeners.filter((l) => l !== listener)
    }
  }, [])

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9)
    const newToast: Toast = {
      id,
      duration: 5000,
      variant: 'default',
      ...toast,
    }
    toasts = [...toasts, newToast]
    emitChange()

    // Auto-remove after duration
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        toasts = toasts.filter((t) => t.id !== id)
        emitChange()
      }, newToast.duration)
    }
  }, [])

  const removeToast = useCallback((id: string) => {
    toasts = toasts.filter((t) => t.id !== id)
    emitChange()
  }, [])

  return {
    toasts,
    addToast,
    removeToast,
  }
}

// Simple hook for components that just need to show toasts
export function useToast() {
  const { addToast, removeToast } = useToastStore()

  const toast = useCallback(
    (options: Omit<Toast, 'id'>) => {
      addToast(options)
    },
    [addToast]
  )

  return { toast, removeToast }
}
